import { Alert, Button, Col, Form, FormInstance, Input, InputNumber, Row, Select, Typography } from 'antd';
import withAuthorization from '../../components/hocs/withAuthorization';
import Page from '../../components/page';
import useCreateCard from '../../hooks/data/useCreateCard';
import useGetCardProducts from '../../hooks/data/useGetCardProducts';
import useToastContext from '../../hooks/useToastContext';
import React, { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { camelCaseObjectToSnakeCase } from '../../utils/camelCaseToSnakeCase';
import { CardsQueryParamsHeaders, CitiesByStateByCountry, Countries, EXCLUDED_COUNTRIES, RealmRoles, StatesByCountry } from '../../constants';
import { CardState, CardType, OwnershipLevelType } from '../../interfaces/card';
import { CountryCode } from '../../interfaces/card-holder';
import { isNil } from 'lodash';

type NewCardFormInputs = {
  cardHolderId: string;
  cardProductId: string;
  cardType: CardType;
  fulfillment: {
    trackingId: string;
    codeCurrier: string;
    shipping: {
      street: string;
      streetNumber: string;
      city: string;
      state: string;
      country: CountryCode;
      postalCode: string;
      floor: string;
      apt: string;
      extraComment: string;
    };
  };
  state: CardState;
  limits: {
    cash: number;
    installments: number;
    extractAtm: number;
    loans: number;
    govermentPlans: number;
    fundingLimit: number;
  };
  closingGroup: number;
  emboss: {
    name: string;
    fourthLine: string;
  };
  ownershipLevel: OwnershipLevelType;
  cardholderParent: string;
  cardParent: string;
  additionalAndHasParentId: boolean;
};

const SubmitButton = ({ form, loading }: { form: FormInstance; loading: boolean }) => {
  const [submittable, setSubmittable] = React.useState(false);

  // Watch all values
  const values = Form.useWatch([], form);

  React.useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmittable(true);
      },
      () => {
        setSubmittable(false);
      }
    );
  }, [values]);

  return (
    <Button type="primary" htmlType="submit" disabled={!submittable} loading={loading}>
      Crear
    </Button>
  );
};

const NewCard = () => {
  const { issuer } = useParams<{ issuer: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToastContext();
  const { cardProducts, isLoading: cardProductsLoading } = useGetCardProducts(1, 100);
  const { isLoading: cardLoading, isError: cardError, createCard } = useCreateCard();
  const [form] = Form.useForm();
  const cardType = Form.useWatch('cardType', form);
  const ownershipLevelValue = searchParams.get(CardsQueryParamsHeaders.OWNERSHIP_LEVEL)
  const country = Form.useWatch(['fulfillment', 'shipping', 'country'], form);
  const state = Form.useWatch(['fulfillment', 'shipping', 'state'], form);

  useEffect(() => {
    form.setFieldValue(['fulfillment', 'shipping', 'state'], null);
    form.setFieldValue(['fulfillment', 'shipping', 'city'], null);
  }, [country]);

  useEffect(() => {
    form.setFieldValue(['fulfillment', 'shipping', 'city'], null);
  }, [state]);

  const onSubmit = async (data: NewCardFormInputs) => {
    try {
      const { fulfillment, ...payloadWithoutFulfillment } = data;
      let payload;
      if (data.cardType === CardType.Physical) {
        payload = { ...payloadWithoutFulfillment, fulfillment };
      } else {
        payload = payloadWithoutFulfillment;
      }

      if(data.ownershipLevel === OwnershipLevelType.Main){
        payload = { ...payload, cardHolderId: searchParams.get(CardsQueryParamsHeaders.CARDHOLDER_ID) }
      }else {
        payload = { ...payload, cardholderParent: searchParams.get(CardsQueryParamsHeaders.CARDHOLDER_PARENT_ID) }
      }

      let cardParentValue = searchParams.get(CardsQueryParamsHeaders.CARD_PARENT)

      if(!isNil(cardParentValue)){
        payload = { ...payload, cardParent: cardParentValue }
      }


      const createCardResult = await createCard(
        camelCaseObjectToSnakeCase(payload)
      );

      if (createCardResult && !cardError) {
        form.resetFields();
        toast.success('Exito', 'Se ha creado una tarjeta nueva.', {
          label: 'Ver',
          onClick: () => navigate(`/${issuer}/card-holders/${createCardResult.data.card_holder_id}`),
        });
      }
    } catch (e: unknown) {
      console.error(e);
      toast.error('Error', 'Ocurrio un error al intentar crear una nueva tarjeta. Por favor reintenta nuevamente');
    }
  };

  return (
    <Page title="Crear tarjeta">
      <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          initialValues={{
            ownershipLevel: ownershipLevelValue, // preset value for the input field
          }}
          onFinish={onSubmit}>
        <Row>
          <Col span={24}>
            <Typography.Title level={2}>Informacion</Typography.Title>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item<NewCardFormInputs>
              name="cardType"
              label="Tipo de Tarjeta"
              rules={[{ required: true, message: 'Seleccionar tipo de tarjeta' }]}
            >
              <Select
                placeholder="Seleccionar tarjeta"
                options={[
                  {
                    label: 'Fisica',
                    value: CardType.Physical,
                  },
                  {
                    label: 'Virtual',
                    value: CardType.Virtual,
                  },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<NewCardFormInputs>
              name="cardProductId"
              label="Producto"
              rules={[{ required: true, message: 'Seleccionar tipo de producto' }]}
            >
              <Select
                loading={cardProductsLoading}
                placeholder="Seleccionar producto"
                options={cardProducts?.map((cardProduct) => ({ label: cardProduct.name, value: cardProduct.name }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
              <Form.Item<NewCardFormInputs>
                  name="ownershipLevel"
                  label="Tipo de titular"
                >
                  <Input disabled={true}/>
                </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            {ownershipLevelValue === OwnershipLevelType.Additional && (
              <Form.Item<NewCardFormInputs>
                name="cardHolderId"
                label="ID del titular"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            )}
          </Col>
        </Row>
        {cardType === CardType.Physical && (
          <>
            <Row>
              <Col span={24}>
                <Typography.Title level={2}>Personalizacion</Typography.Title>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item<NewCardFormInputs>
                  name={['emboss', 'name']}
                  label="Nombre de la tarjeta"
                  rules={[
                    { required: true, message: 'Agregar un nombre.' },
                    { max: 19, message: 'Maximo 19 caracteres.' },
                  ]}
                >
                  <Input showCount maxLength={19} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item<NewCardFormInputs>
                  name={['emboss', 'fourthLine']}
                  label="Cuarta linea"
                  rules={[{ max: 19, message: 'Maximo 19 caracteres.' }]}
                >
                  <Input showCount maxLength={19} />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Typography.Title level={2}>Direccion de Envio</Typography.Title>
              </Col>
            </Row>
            <Row>
              <Col span={16}>
                <Alert
                  type="info"
                  message="Seleccionaste tipo de tarjeta fisica. Para continuar, deberas completar la siguiente informacion."
                  showIcon
                  closable
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item<NewCardFormInputs>
                  name={['fulfillment', 'trackingId']}
                  label="Numero de Seguimiento"
                  rules={[{ required: true, message: 'Agregar numero de seguimiento.' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item<NewCardFormInputs>
                  name={['fulfillment', 'codeCurrier']}
                  label="Codigo de distribucion del correo"
                  rules={[{ required: true, message: 'Agregar codigo distribucion.' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item<NewCardFormInputs>
                  name={['fulfillment', 'shipping', 'street']}
                  label="Calle"
                  rules={[{ required: true, message: 'Agregar calle.' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item<NewCardFormInputs>
                  name={['fulfillment', 'shipping', 'streetNumber']}
                  label="Numero"
                  rules={[{ required: true, message: 'Agregar numero de calle.' }]}
                >
                  <InputNumber controls={false} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item<NewCardFormInputs> name={['fulfillment', 'shipping', 'apt']} label="Departamento">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item<NewCardFormInputs>
                  name={['fulfillment', 'shipping', 'country']}
                  label="Pais"
                  rules={[{ required: true, message: 'Por favor ingresar pais.' }]}
                >
                  <Select
                    placeholder="Seleccionar pais"
                    showSearch
                    options={Object.entries(Countries)
                      .filter(([code]) => !EXCLUDED_COUNTRIES.includes(code))
                      .map(([code, name]) => ({ label: name, value: code }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item<NewCardFormInputs>
                  name={['fulfillment', 'shipping', 'state']}
                  label="Estado"
                  rules={[{ required: true, message: 'Por favor ingresar estado.' }]}
                >
                  <Select
                    placeholder="Seleccionar estado"
                    showSearch
                    options={Object.entries(StatesByCountry?.[country] ?? {}).map(([stateCode, name]) => ({
                      label: name,
                      value: stateCode,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item<NewCardFormInputs>
                  name={['fulfillment', 'shipping', 'city']}
                  label="Ciudad"
                  rules={[{ required: true, message: 'Por favor ingresar ciudad.' }]}
                >
                  <Select
                    placeholder="Seleccionar ciudad"
                    showSearch
                    options={CitiesByStateByCountry?.[country]?.[state]?.toSorted()?.map((name) => ({
                      label: name,
                      value: name,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item<NewCardFormInputs>
                  name={['fulfillment', 'shipping', 'postalCode']}
                  label="Codigo Postal"
                  rules={[{ required: true, message: 'Ingresar codigo postal.' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item<NewCardFormInputs>
                  name={['fulfillment', 'shipping', 'extraComment']}
                  label="Comentarios extras"
                >
                  <Input.TextArea allowClear />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
        <Row>
          <Col span={24} style={{ textAlign: 'center' }}>
            <SubmitButton form={form} loading={cardLoading} />
          </Col>
        </Row>
      </Form>
    </Page>
  );
};

export default withAuthorization([RealmRoles.CardsAllWrite], NewCard);
